import { Controller } from "@hotwired/stimulus";
import { blocksFoundingPurchase, foundingOptions, managementURL, revenueCatFor, tipPackage } from "../membership.js";
import { loadScript } from "../script_loader.js";
import { MembershipCheckout } from "../membership_checkout.js";
import Sentry from "../sentry.js";

class SessionChangedError extends Error {}

export default class extends Controller {
  static targets = [
    "offer",
    "price",
    "slider",
    "minimum",
    "maximum",
    "join",
    "tip",
    "management",
    "manage",
    "feedback",
    "loading",
    "message",
    "retry",
    "refresh",
    "reload",
    "checkout",
    "checkoutShell",
    "closeCheckout",
    "progress",
    "progressHeading",
    "progressMessage",
    "progressSpinner",
    "progressSuccess",
    "progressDismiss",
  ];
  static values = {
    apiKey: String,
    userId: String,
    email: String,
    sdkUrl: String,
    statusUrl: String,
    refreshUrl: String,
    pageUrl: String,
    active: Boolean,
    blocked: Boolean,
  };

  connect() {
    this.connection = Symbol();
    this.requests = new Set();
    this.options = [];
    this.loading = false;
    this.busy = false;
    this.ready = false;
    this.failed = false;
    this.refreshing = false;
    this.sessionChanged = false;
    this.navigating = false;
    this.customerInfo = null;
    this.tipPackage = null;
    this.tipPending = false;
    this.membershipPurchased = false;
    this.purchaseKind = null;
    this.progressState = null;
    this.progressDismissed = false;
    this.progressTimer = null;
    this.checkoutBackground = [];
    this.checkoutShellTarget.hidden = true;
    this.checkoutTarget.hidden = true;
    this.checkoutTarget.replaceChildren();
    this.server = { active: this.activeValue, checkout_blocked: this.blockedValue };
    this.pending = this.readPending();
    this.polls = 0;
    if (this.apiKeyValue) void this.load();
  }

  disconnect() {
    this.connection = null;
    clearTimeout(this.pollTimer);
    this.hideCheckout();
    this.hideProgress();
    this.requests.forEach((request) => request.abort());
    void this.checkout?.dispose();
  }

  async load() {
    if (this.loading || this.busy) return;
    const connection = this.connection;
    this.loading = true;
    this.ready = false;
    this.failed = false;
    this.showMessage("");
    this.render();
    try {
      const server = await this.readStatus();
      if (!this.connected(connection)) return;
      this.updateStatus(server);
      await loadScript(this.sdkUrlValue, "module");
      if (!this.connected(connection)) return;
      const purchases = await revenueCatFor(window.RevenueCat, this.apiKeyValue, this.userIdValue);
      if (!this.connected(connection)) return;
      this.purchases = purchases;
      const [info, offerings] = await Promise.all([purchases.getCustomerInfo(), purchases.getOfferings()]);
      if (!this.connected(connection)) return;
      this.customerInfo = info;
      if (this.server.checkout_blocked || blocksFoundingPurchase(info) || this.pending) {
        const refreshed = await this.readStatus(true);
        if (!this.connected(connection)) return;
        this.updateStatus(refreshed);
      }
      this.options = foundingOptions(offerings);
      this.tipPackage = tipPackage(offerings);
      this.sliderTarget.max = Math.max(0, this.options.length - 1);
      this.sliderTarget.value = Math.max(
        0,
        this.options.findIndex((option) => option.amount === 50)
      );
      this.selectAmount();
      this.ready = true;
      if (this.server.active) {
        this.clearPending();
        if (!this.activeValue) return this.confirmMembership();
      } else if (!this.server.reconciled && blocksFoundingPurchase(info) && !this.server.checkout_blocked) {
        this.pending ||= "existing";
      }
      this.refreshMembershipPage();
      this.failed = (!this.options.length && !this.ownsMembership) || !this.tipPackage;
      this.showMessage(
        this.pending
          ? this.pendingMessage()
          : this.failed
            ? "Payment options could not all be loaded. Please try again."
            : "",
        this.pending === "paid" ? "success" : "info"
      );
      this.schedulePoll();
    } catch (error) {
      if (this.connected(connection))
        this.handleError(error, "We couldn't load your payment options. Please try again.");
    } finally {
      if (this.connected(connection)) {
        this.loading = false;
        this.render();
      }
    }
  }

  selectAmount() {
    const option = this.selectedOption;
    this.priceTarget.textContent = option?.price ?? "...";
    this.minimumTarget.textContent = this.options[0]?.price ?? "";
    this.maximumTarget.textContent = this.options.at(-1)?.price ?? "";
    this.sliderTarget.setAttribute(
      "aria-valuetext",
      option ? `${option.price} per year` : "Payment options are loading."
    );
  }

  purchaseMembership() {
    if (!this.ownsMembership) void this.purchase(this.selectedOption?.package, "membership");
  }

  purchaseTip() {
    void this.purchase(this.tipPackage, "tip");
  }

  async purchase(pkg, kind) {
    if (!pkg || !this.ready || this.busy || this.loading || this.sessionChanged || this.progressTarget.open) return;
    const connection = this.connection;
    let checkout;
    this.checkoutFocus = document.activeElement;
    this.busy = true;
    this.purchaseKind = kind;
    this.failed = false;
    this.hideProgress();
    this.progressDismissed = false;
    this.showMessage("");
    this.render();
    try {
      await this.checkout?.dispose();
      if (!this.connected(connection)) return;
      const server = await this.readStatus(kind === "membership");
      if (!this.connected(connection)) return;
      this.updateStatus(server);
      this.tipBaseline = server.latest_tip_id;
      if (this.purchases.getAppUserId() !== this.userIdValue) throw new SessionChangedError();
      if (kind === "membership") {
        const info = await this.purchases.getCustomerInfo();
        if (!this.connected(connection)) return;
        this.customerInfo = info;
        if (this.ownsMembership) {
          this.pending ||= !this.server.checkout_blocked && "existing";
          this.showMessage(
            this.pending ? this.pendingMessage() : "You already have a membership. You can manage it below."
          );
          if (this.server.active && !this.activeValue) this.confirmMembership();
          return;
        }
      }
      this.checkoutShellTarget.hidden = false;
      this.checkoutTarget.hidden = false;
      this.disableCheckoutBackground();
      this.closeCheckoutTarget.focus();
      const accent = window.getComputedStyle(this.element).getPropertyValue("--color-cta").trim();
      checkout = new MembershipCheckout({
        purchases: this.purchases,
        errorCodes: window.RevenueCat.ErrorCode,
        element: this.checkoutTarget,
        onConfirming: () => {
          if (this.connected(connection)) this.showProgress("confirming");
        },
        onSuccess: (result) => {
          if (this.connected(connection) && this.checkout === checkout) {
            this.purchaseSucceeded(result, kind);
            void this.refreshMembership();
          }
        },
        onError: (error) => {
          if (this.connected(connection)) this.handlePurchaseError(error, kind, checkout);
        },
      });
      this.checkout = checkout;
      await checkout.start({
        rcPackage: pkg,
        customerEmail: this.emailValue,
        skipSuccessPage: true,
        brandingAppearanceOverride: accent ? { color_accent: accent } : undefined,
      });
    } catch (error) {
      if (this.connected(connection)) this.handlePurchaseError(error, kind, checkout);
    } finally {
      if (this.connected(connection)) {
        this.busy = false;
        this.hideCheckout();
        this.checkoutTarget.hidden = true;
        if (!this.paymentConfirmed) this.hideProgress();
        this.render();
        if (!this.progressTarget.open) this.restoreCheckoutFocus();
        if (this.pending || this.tipPending) void this.refreshMembership();
        this.refreshMembershipPage();
      }
    }
  }

  handlePurchaseError(error, kind, checkout) {
    if (checkout?.state === "canceled") {
      this.showMessage(this.pending ? this.pendingMessage() : "");
    } else if (checkout?.state === "pending") {
      if (kind === "membership") {
        this.savePending("processing");
        this.showMessage(this.pendingMessage());
      } else {
        this.tipPending = true;
        this.polls = 0;
        this.showMessage("Your tip is still being processed. Please check your payment email before trying again.");
      }
    } else {
      if (checkout?.mounted && !(error instanceof SessionChangedError)) {
        if (kind === "membership") this.savePending("processing");
        else this.tipPending = true;
      }
      this.handleError(
        error,
        checkout?.mounted
          ? "We couldn't confirm your payment. Please check your payment email before trying again."
          : "We couldn't open checkout. Please try again."
      );
    }
  }

  purchaseSucceeded(result, kind) {
    this.customerInfo = result.customerInfo;
    this.polls = 0;
    if (kind === "membership") {
      this.membershipPurchased = true;
      this.savePending("paid");
      this.showMessage(this.pendingMessage(), "success");
      this.showProgress("membership");
    } else {
      this.tipPending = false;
      this.showMessage("Thanks for your tip! Your support helps us keep building Peptide Tracker.", "success");
      this.showProgress("tip");
    }
  }

  async refreshMembership(event) {
    if (this.refreshing || this.busy || this.loading || this.sessionChanged) return;
    const connection = this.connection;
    this.refreshing = true;
    this.render();
    try {
      const server = await this.readStatus(Boolean(event) || (this.pending && this.polls > 0 && this.polls % 5 === 0));
      if (!this.connected(connection)) return;
      this.updateStatus(server);
      if (this.tipPending && server.latest_tip_id && server.latest_tip_id !== this.tipBaseline) {
        this.purchaseSucceeded({ customerInfo: this.customerInfo }, "tip");
      } else if (this.tipPending) {
        this.showMessage(
          "Your tip is still being processed. You can keep using the app and check your payment email before trying again."
        );
      }
      if (this.server.active) this.confirmMembership();
      else if (this.pending) this.showMessage(this.pendingMessage(), this.pending === "paid" ? "success" : "info");
      this.refreshMembershipPage();
      if (this.polls >= 30 && this.progressState === "membership")
        this.progressMessageTarget.textContent = this.pendingMessage();
    } catch (error) {
      if (this.connected(connection))
        this.handleError(
          error,
          "We couldn't refresh your membership. Your payment will not be repeated. Please try refreshing again."
        );
    } finally {
      if (this.connected(connection)) {
        this.refreshing = false;
        this.render();
        this.schedulePoll();
      }
    }
  }

  schedulePoll() {
    clearTimeout(this.pollTimer);
    if ((!this.pending && !this.tipPending) || this.polls >= 30 || this.sessionChanged || !this.connection) return;
    this.pollTimer = setTimeout(() => {
      this.polls += 1;
      void this.refreshMembership();
    }, 2000);
  }

  updateStatus(server) {
    this.server = server;
    if (server.reconciled && server.status === "expired") {
      this.membershipPurchased = false;
      if (this.pending === "paid") {
        this.clearPending();
        this.showMessage("Your membership has ended. You can start a new membership below.");
        if (this.progressState === "membership")
          this.progressMessageTarget.textContent = this.messageTarget.textContent;
      }
    }
  }

  confirmMembership() {
    this.membershipPurchased = false;
    this.clearPending();
    clearTimeout(this.pollTimer);
    if (this.progressState === "membership") this.showProgress("membership_ready");
    this.refreshMembershipPage();
  }

  refreshMembershipPage() {
    const changed = this.server.active !== this.activeValue || this.server.checkout_blocked !== this.blockedValue;
    if (this.navigating || !changed || this.busy || this.progressTarget.open) return;
    this.navigating = true;
    window.Turbo.cache.clear();
    window.Turbo.visit(this.pageUrlValue, { action: "replace" });
  }

  async readStatus(reconcile = false) {
    const request = new AbortController();
    this.requests.add(request);
    try {
      const response = await fetch(reconcile ? this.refreshUrlValue : this.statusUrlValue, {
        method: reconcile ? "POST" : "GET",
        headers: {
          Accept: "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content ?? "",
        },
        credentials: "same-origin",
        cache: "no-store",
        signal: request.signal,
      });
      if (response.redirected || [401, 403].includes(response.status)) throw new SessionChangedError();
      if (!response.ok) throw new Error(`Membership status returned ${response.status}.`);
      const { data } = await response.json();
      if (data.user_id !== this.userIdValue) throw new SessionChangedError();
      return { ...data, reconciled: data.reconciled || this.server.reconciled };
    } finally {
      this.requests.delete(request);
    }
  }

  retry() {
    void this.load();
  }

  handleError(error, message) {
    if (!this.paymentConfirmed) this.hideProgress();
    if (error instanceof SessionChangedError) {
      this.sessionChanged = true;
      this.ready = false;
      clearTimeout(this.pollTimer);
      this.showMessage("Your sign-in has changed. Reload this page before making a payment.", "error");
    } else {
      this.failed = true;
      this.showMessage(message, "error");
      Sentry.captureException(error, { tags: { scope: "web_membership" } });
    }
    if (this.paymentConfirmed) this.progressMessageTarget.textContent = this.messageTarget.textContent;
  }

  render() {
    this.offerTarget.hidden = this.ownsMembership;
    this.managementTarget.hidden = !this.ownsMembership;
    this.joinTarget.disabled = !this.ready || this.busy || this.ownsMembership || !this.selectedOption;
    this.sliderTarget.disabled = this.joinTarget.disabled;
    this.tipTarget.disabled = !this.ready || this.busy || !this.tipPackage;
    const tipPrice = this.tipPackage?.webBillingProduct.price.formattedPrice;
    this.tipTarget.textContent = tipPrice ? `Leave a ${tipPrice} tip` : "Leave a tip";
    const url = managementURL(this.customerInfo);
    this.manageTarget.hidden = !url || this.sessionChanged;
    if (url) this.manageTarget.href = url;
    else this.manageTarget.removeAttribute("href");
    this.retryTarget.hidden = !this.failed || this.loading || this.busy || this.sessionChanged;
    this.refreshTarget.hidden = (!this.pending && !this.tipPending) || this.sessionChanged;
    this.refreshTarget.textContent = this.tipPending ? "Refresh payment status" : "Refresh membership";
    this.refreshTarget.disabled = this.refreshing || this.busy || this.loading;
    this.reloadTarget.hidden = !this.sessionChanged;
    this.feedbackTarget.hidden = !this.messageTarget.textContent || this.progressTarget.open;
    this.loadingTarget.hidden = !(this.loading || (this.busy && this.purchaseKind === "membership"));
    this.element.setAttribute("aria-busy", Boolean(this.loading || this.busy));
  }

  showMessage(message, kind = "info") {
    this.messageTarget.textContent = message;
    this.feedbackTarget.dataset.kind = kind;
    this.feedbackTarget.hidden = !message || this.progressTarget.open;
  }

  showProgress(state) {
    this.hideCheckout();
    this.progressState = state;
    const [heading, message] = {
      confirming: ["Your payment is being confirmed.", "Please keep this page open while we check the result."],
      membership: [
        "Your payment was confirmed.",
        "We're updating your founding membership. This can take a few moments.",
      ],
      membership_ready: [
        "Your payment was confirmed.",
        "Thanks for being a Founding Member! Your membership is now active.",
      ],
      tip: ["Your payment was confirmed.", "Thanks for your tip! Your support helps us keep building Peptide Tracker."],
    }[state];
    this.progressHeadingTarget.textContent = heading;
    this.progressMessageTarget.textContent = message;
    this.progressSpinnerTarget.hidden = this.paymentConfirmed;
    this.progressSuccessTarget.hidden = !this.paymentConfirmed;
    this.progressDismissTarget.textContent = this.paymentConfirmed ? "Done" : "Return to support";
    if (state === "confirming") this.showMessage(`${heading} ${message}`);
    if (this.paymentConfirmed) {
      clearTimeout(this.progressTimer);
      this.progressTimer = null;
      if (!this.progressDismissed && !this.progressTarget.open) this.openProgress();
      return;
    }
    if (this.progressDismissed || this.progressTarget.open || this.progressTimer !== null) return;
    this.progressTimer = setTimeout(() => {
      this.progressTimer = null;
      this.openProgress();
    }, 500);
  }

  // The dialog is the single confirmation surface, so the feedback banner steps aside
  // while it is open and comes back on dismissal if a message still needs a home.
  openProgress() {
    this.progressTarget.showModal();
    this.feedbackTarget.hidden = true;
  }

  hideProgress() {
    clearTimeout(this.progressTimer);
    this.progressTimer = null;
    this.progressState = null;
    if (this.progressTarget.open) this.progressTarget.close();
    this.feedbackTarget.hidden = !this.messageTarget.textContent;
  }

  dismissProgress(event) {
    event?.preventDefault();
    this.progressDismissed = true;
    this.hideProgress();
    this.restoreCheckoutFocus();
    this.feedbackTarget.scrollIntoView({ block: "center", behavior: "smooth" });
    this.refreshMembershipPage();
  }

  closeCheckout(event) {
    if (this.checkoutShellTarget.hidden) return;
    event?.preventDefault();
    this.hideCheckout();
    this.checkout?.cancel();
  }

  hideCheckout() {
    this.checkoutShellTarget.hidden = true;
    this.checkoutBackground.forEach((element) => (element.inert = false));
    this.checkoutBackground = [];
  }

  disableCheckoutBackground() {
    // Stripe adds authentication overlays to the body, so only the existing dashboard should become inert.
    for (let element = this.checkoutShellTarget; element.parentElement; element = element.parentElement) {
      for (const sibling of element.parentElement.children) {
        if (sibling !== element && sibling instanceof HTMLElement && !sibling.inert) {
          sibling.inert = true;
          this.checkoutBackground.push(sibling);
        }
      }
      if (element.parentElement === document.body) break;
    }
  }

  restoreCheckoutFocus() {
    if (this.checkoutFocus?.isConnected && !this.checkoutFocus.disabled) this.checkoutFocus.focus();
    else if (this.pending && !this.refreshTarget.disabled) this.refreshTarget.focus();
  }

  pendingMessage() {
    if (this.pending === "existing")
      return "We found an existing membership, but couldn't confirm its status on your account. Please contact support if this continues.";
    if (this.pending === "paid" && this.polls >= 30)
      return "Your payment was confirmed. Your membership is taking longer to update. You can keep using the app and refresh its status below.";
    if (this.pending === "processing")
      return "Your payment has not been confirmed yet. You can keep using the app and refresh its status. Please check your payment email before trying again.";
    return this.pending === "paid"
      ? "Your payment was confirmed. Your membership is updating. You can refresh its status below."
      : "Your membership is being updated. Please check your payment email or refresh its status below.";
  }

  savePending(state) {
    this.pending = state;
    try {
      sessionStorage.setItem(this.pendingKey, JSON.stringify({ state, at: Date.now() }));
    } catch {}
  }

  readPending() {
    try {
      const saved = JSON.parse(sessionStorage.getItem(this.pendingKey));
      return saved && Date.now() - saved.at < 86400000 ? saved.state : null;
    } catch {
      return null;
    }
  }

  clearPending() {
    this.pending = null;
    try {
      sessionStorage.removeItem(this.pendingKey);
    } catch {}
  }

  connected(connection) {
    return this.connection === connection && this.element.isConnected;
  }

  get pendingKey() {
    return `peptide_tracker.membership.pending.${this.apiKeyValue.startsWith("rcb_sb_") ? "sandbox" : "production"}.${this.userIdValue}`;
  }

  get selectedOption() {
    return this.options[Number(this.sliderTarget.value)];
  }

  get paymentConfirmed() {
    return ["membership", "membership_ready", "tip"].includes(this.progressState);
  }

  get ownsMembership() {
    return Boolean(
      this.membershipPurchased ||
      this.server.checkout_blocked ||
      this.server.payment_pending ||
      (!this.server.reconciled && blocksFoundingPurchase(this.customerInfo))
    );
  }
}
